import React, { useState, useEffect } from "react";
import {
  Drawer,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  IconButton,
  Box,
  useTheme,
  useMediaQuery,
  styled,
} from "@mui/material";
import {
  Menu as MenuIcon,
  Dashboard,
  Assessment,
  Summarize,
  Build,
  ChangeCircle,
  Apartment as ApartmentIcon,
  Support as SupportIcon,
  PersonPin,
  Description as DescriptionIcon,
  ChevronLeft,
  Chat,
  LockOutlined,
} from "@mui/icons-material";
import { useNavigate, useLocation, useParams } from "react-router-dom";
import axios from "axios";

// Custom styled components
const StyledDrawer = styled(Drawer)(({ theme, open }) => ({
  width: open ? 240 : 60,
  flexShrink: 0,
  "& .MuiDrawer-paper": {
    width: open ? 240 : 60,
    boxSizing: "border-box",
    background: "#009797",
    boxShadow: "4px 0px 10px rgba(0, 0, 0, 0.25)",
    transition: theme.transitions.create(["width", "transform"], {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen,
    }),
    overflowX: "hidden",
  },
}));

const StyledListItem = styled(ListItem)(({ theme, selected }) => ({
  marginBottom: theme.spacing(2),
  color: "#FFFFFF",
  "&:hover": {
    backgroundColor: "rgba(255, 255, 255, 0.1)",
  },
  "& .MuiListItemText-primary": {
    fontFamily: "Poppins",
    fontSize: "18px",
    fontWeight: selected ? 600 : 400,
    color: "#FFFFFF",
  },
  "& .MuiListItemText-secondary": {
    fontFamily: "Poppins",
    fontSize: "14px",
    color: "rgba(255, 255, 255, 0.7)",
  },
  "& .MuiListItemIcon-root": {
    minWidth: 40,
    color: "#FFFFFF",
    "& img": {
      width: 24,
      height: 24,
      filter: "brightness(0) invert(1)",
    },
  },
}));

// Define minimum required tier for each item
// 0 = Free, 1 = Standard, 2 = Platinum
const menuItemsConfig = [
  {
    text: "Dashboard",
    icon: <Dashboard />,
    pathSuffix: "dashboard",
    minTier: 0,
  },
  {
    text: "Career Report",
    icon: <Assessment />,
    pathSuffix: "report",
    minTier: 0,
  },
  {
    text: "Career Summary",
    icon: <Summarize />,
    pathSuffix: "summary",
    minTier: 0,
  },
  {
    text: "Ask Caroline",
    icon: <Chat />,
    pathSuffix: "ai-chat",
    minTier: 2, // Platinum Only
  },

  {
    text: "Job Hunting Toolkit",
    icon: <Build />,
    pathSuffix: "cheat-sheets",
    minTier: 0,
  },
  {
    text: "Making Your Mid-Career Switch",
    icon: <ChangeCircle />,
    pathSuffix: "mid-career-switching",
    minTier: 0,
  },
  {
    text: "Land Your Dream Job",
    icon: <ApartmentIcon />,
    pathSuffix: "land-your-dream-job",
    minTier: 0,
  },
  {
    text: "Resume Samples",
    icon: <DescriptionIcon />,
    pathSuffix: "resume-templates",
    minTier: 0,
  },

  {
    text: "Personalised Coaching",
    icon: <PersonPin />,
    pathSuffix: "special-coaching",
    minTier: 0,
  },
  {
    text: "Support",
    icon: <SupportIcon />,
    pathSuffix: "support",
    minTier: 0,
  },
];

const NavigationSidebar = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [open, setOpen] = useState(!isMobile);
  const navigate = useNavigate();
  const location = useLocation();
  const { userId } = useParams();

  // State for user tier and subscription status
  const [userTier, setUserTier] = useState(0); // Default to Free
  const [isLoadingStatus, setIsLoadingStatus] = useState(true);
  const [checkingAnswers, setCheckingAnswers] = useState("");

  useEffect(() => {
    const checkUpgradeStatus = async () => {
      if (!userId) {
        setIsLoadingStatus(false);
        return;
      }

      setIsLoadingStatus(true);
      try {
        // Fetch tier and subscription end date from the updated endpoint
        const response = await axios.get(
          `${process.env.REACT_APP_API_URL}/user/${userId}/upgrade-status`
        );

        let currentTier = response.data.tier; // 0, 1, or 2
        const subEndDateStr = response.data.subscriptionEndDate;

        // Check if Platinum subscription expired
        if (currentTier === 2 && subEndDateStr) {
          const subEndDate = new Date(subEndDateStr);
          if (subEndDate <= new Date()) {
            currentTier = 0; // Downgrade effective tier to Free if expired
          }
        }

        setUserTier(currentTier);
      } catch (error) {
        console.error("Error checking upgrade status:", error);
        setUserTier(0); // Default to Free on error
      } finally {
        setIsLoadingStatus(false);
      }
    };

    checkUpgradeStatus();
  }, [userId]);

  // Helper to check if user has answers before navigation
  const handleProtectedSidebarClick = async (item) => {
    setCheckingAnswers(item.pathSuffix);
    try {
      const res = await axios.get(
        `${process.env.REACT_APP_API_URL}/user/${userId}/has-answers`
      );
      if (res.data.hasAnswers) {
        navigate(`/${item.pathSuffix}/${userId}`);
      } else {
        navigate(`/questions/${userId}`);
      }
      if (isMobile) setOpen(false);
    } catch (e) {
      navigate(`/questions/${userId}`);
      if (isMobile) setOpen(false);
    } finally {
      setCheckingAnswers("");
    }
  };

  const handleItemClick = (item) => {
    const effectiveTier = getEffectiveTier(userTier);
    const hasAccess = effectiveTier >= item.minTier;
    const needsAnswersCheck = ["report", "summary", "ai-chat"].includes(
      item.pathSuffix
    );
    if (hasAccess) {
      if (needsAnswersCheck) {
        handleProtectedSidebarClick(item);
      } else {
        navigate(`/${item.pathSuffix}/${userId}`);
        if (isMobile) setOpen(false);
      }
    } else {
      navigate(`/benefits/${userId}`);
      if (isMobile) setOpen(false);
    }
  };

  // Convert any tier to its effective base tier (0=Free, 1=Standard, 2=Platinum)
  const getEffectiveTier = (tier) => {
    // Map monthly tiers to their annual equivalents
    switch (tier) {
      case 0:
        return 0; // Free
      case 1:
        return 1; // Standard Annual
      case 2:
        return 2; // Platinum Annual
      case 3:
        return 1; // Standard Monthly -> maps to Standard features
      case 4:
        return 2; // Platinum Monthly -> maps to Platinum features
      case 5:
        return 1; // Limited Annual -> maps to Standard features
      case 6:
        return 1; // Limited Monthly -> maps to Standard features
      default:
        return 0; // Default to Free for safety
    }
  };

  return (
    <>
      {/* Mobile Menu Button - Always visible on mobile */}
      {isMobile && !open && (
        <IconButton
          onClick={() => setOpen(true)}
          sx={{
            position: "fixed",
            left: 16,
            top: 16,
            zIndex: theme.zIndex.drawer + 2,
            bgcolor: "background.paper",
            boxShadow: 1,
            "&:hover": { bgcolor: "action.hover" },
          }}
        >
          <MenuIcon />
        </IconButton>
      )}

      <StyledDrawer
        variant={isMobile ? "temporary" : "permanent"}
        open={open}
        onClose={() => setOpen(false)}
      >
        <Box
          sx={{
            mt: 2,
            display: "flex",
            justifyContent: "flex-end",
            pr: 1,
          }}
        >
          <IconButton
            onClick={() => setOpen(!open)}
            sx={{
              color: "#FFFFFF",
              "&:hover": {
                backgroundColor: "rgba(255, 255, 255, 0.1)",
              },
            }}
          >
            {open ? <ChevronLeft /> : <MenuIcon />}
          </IconButton>
        </Box>

        <List sx={{ mt: 2 }}>
          {isLoadingStatus ? (
            // Optional: Show a loading indicator
            <ListItem>
              <ListItemIcon>
                <MenuIcon />
              </ListItemIcon>
            </ListItem>
          ) : (
            menuItemsConfig.map((item) => {
              // Get the effective tier for comparing with minTier
              const effectiveTier = getEffectiveTier(userTier);

              // Determine if the item should be locked - All items simply check against minTier
              const isLocked = effectiveTier < item.minTier;

              return (
                <StyledListItem
                  button
                  key={item.text}
                  onClick={() => handleItemClick(item)}
                  selected={location.pathname.startsWith(
                    `/${item.pathSuffix}/${userId}`
                  )}
                  sx={{
                    opacity: isLocked ? 0.6 : 1, // Dim locked items
                    cursor: "pointer", // Use pointer cursor for all items
                  }}
                >
                  <ListItemIcon>
                    {isLocked ? <LockOutlined /> : item.icon}
                  </ListItemIcon>
                  {open && (
                    <ListItemText
                      primary={item.text}
                      secondary={
                        isLocked
                          ? "There's more to see - Click to Upgrade"
                          : null
                      }
                    />
                  )}
                </StyledListItem>
              );
            })
          )}
        </List>
      </StyledDrawer>
    </>
  );
};

export default NavigationSidebar;
